import { formatMinor, formatRateBp, registrationStatus, type VendorDetail } from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { bookkeepingReferenceQuery } from '../../shared/api/bookkeeping-queries.js';
import { AtAGlancePanel, ObjectTimeline, RelationshipsPanel } from '../objects/object-panels.js';
import { sessionQuery } from '../../shared/api/queries.js';
import {
  addVendorAlias,
  removeVendorAlias,
  mergeVendors,
  setVendorTaxInfo,
  updateVendor,
  vendorKeys,
  vendorQuery,
  vendorsQuery,
  verifyVendorTaxInfo,
} from '../../shared/api/vendor-queries.js';

/**
 * One supplier.
 *
 * Three things sit on this page and they are deliberately not mixed: what
 * somebody decided (the default account), what the system observed (spend,
 * frequency, the spellings it has learned), and what the CRA cares about (the
 * registration, and whether anybody has checked it).
 */

export function VendorDetailPage() {
  const { vendorId } = useParams({ from: '/protected/suppliers/$vendorId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();

  const vendor = useQuery(vendorQuery(organizationId, vendorId));
  const reference = useQuery(bookkeepingReferenceQuery(organizationId));
  const others = useQuery(vendorsQuery(organizationId));

  const [error, setError] = useState<string | null>(null);
  const [justArchived, setJustArchived] = useState(false);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: vendorKeys.all(organizationId) });
  };
  const onError = (caught: unknown) =>
    setError(caught instanceof Error ? caught.message : 'That did not work.');

  const update = useMutation({
    mutationFn: (body: Parameters<typeof updateVendor>[2]) =>
      updateVendor(organizationId, vendorId, body),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError,
  });

  if (vendor.isPending)
    return (
      <div className="flex flex-col gap-3" aria-label="Loading">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (vendor.isError) {
    return (
      <Alert tone="danger" title="That supplier could not be loaded">
        {vendor.error instanceof Error ? vendor.error.message : 'Unknown error.'}
      </Alert>
    );
  }

  const data: VendorDetail = vendor.data;
  const status = registrationStatus(data);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/suppliers" className="text-sm text-content-muted hover:text-content">
          ← Suppliers
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">{data.displayName}</h1>
        <p className="text-sm text-content-muted">
          {data.recordCount === 0
            ? 'No confirmed records yet.'
            : `${data.recordCount} record${data.recordCount === 1 ? '' : 's'} since ${data.firstRecordedOn ?? 'the start'}`}
          {data.archivedAt === null ? '' : ' · archived'}
        </p>
      </div>

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {justArchived && data.archivedAt !== null ? (
        <Alert tone="info">
          <span className="flex flex-wrap items-center gap-3">
            <span>
              {data.displayName} is archived. Nothing was deleted, their records and history are
              exactly where they were.
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                update.mutate({ archived: false });
                setJustArchived(false);
              }}
            >
              Undo
            </Button>
          </span>
        </Alert>
      ) : null}

      <Alert tone={status.tone === 'success' ? 'success' : status.tone}>{status.label}</Alert>

      <Link
        to="/suppliers/$vendorId/memory"
        params={{ vendorId: data.id }}
        className="text-sm text-brand-600 hover:underline"
      >
        What the system has observed about this supplier →
      </Link>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label="Total"
          value={data.totalAmountMinor === null ? '-' : formatMinor(data.totalAmountMinor)}
        />
        <Stat
          label="Typical"
          value={data.meanAmountMinor === null ? '-' : formatMinor(data.meanAmountMinor)}
        />
        <Stat label="Usual HST" value={formatRateBp(data.meanTaxRateBp)} />
        <Stat label="Credits claimed" value={formatMinor(data.itcClaimedMinor)} />
      </div>

      {/* Every field a person can set, in one place, each saving on blur.
          Autosave is right here and wrong on the bookkeeping editor: a phone
          number is a fact being recorded, not a judgement being made. */}
      <Card>
        <CardHeader
          title="Details"
          description="Everything about this supplier. Changes save as you leave each field."
        />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Name"
            hint="What you call them."
            value={data.displayName}
            onCommit={(value) => {
              if (value.trim().length > 0) update.mutate({ displayName: value.trim() });
            }}
          />
          <TextField
            label="Legal name"
            hint="If the invoice says something different."
            value={data.legalName}
            onCommit={(value) => update.mutate({ legalName: value === '' ? null : value })}
          />
          <TextField
            label="Mailing address"
            hint="Printed on the T5018 slip when this supplier is a subcontractor."
            className="sm:col-span-2"
            value={data.mailingAddress}
            onCommit={(value) => update.mutate({ mailingAddress: value === '' ? null : value })}
          />
          <TextField
            label="Email"
            value={data.email}
            onCommit={(value) => update.mutate({ email: value === '' ? null : value })}
          />
          <TextField
            label="Phone"
            value={data.phone}
            onCommit={(value) => update.mutate({ phone: value === '' ? null : value })}
          />
          <TextField
            label="Website"
            className="sm:col-span-2"
            value={data.website}
            onCommit={(value) => update.mutate({ website: value === '' ? null : value })}
          />

          <Field label="Default account">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={data.defaultCategoryId ?? ''}
                onChange={(event) =>
                  update.mutate({
                    defaultCategoryId: event.target.value === '' ? null : event.target.value,
                  })
                }
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">Not set</option>
                {(reference.data?.categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.code === null ? category.name : `${category.code}, ${category.name}`}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <TextField
            label="Notes"
            className="sm:col-span-2"
            value={data.notes}
            onCommit={(value) => update.mutate({ notes: value === '' ? null : value })}
          />
        </CardBody>
      </Card>

      {/* Archived, never deleted: records and learned rules point here, and a
          supplier who disappeared would take their history with them. */}
      <Card>
        <CardHeader
          title={data.archivedAt === null ? 'Archive this supplier' : 'This supplier is archived'}
          description={
            data.archivedAt === null
              ? 'They stop appearing in lists and suggestions. Their records and history stay exactly where they are.'
              : `Archived ${data.archivedAt.slice(0, 10)}. Nothing was deleted.`
          }
          actions={
            data.archivedAt === null ? (
              // No confirmation dialog. A confirmation interrupts everybody to
              // guard against a rare mistake; undo protects only the person who
              // made it, and archiving is reversible by construction.
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  update.mutate({ archived: true });
                  setJustArchived(true);
                }}
              >
                Archive
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  update.mutate({ archived: false });
                  setJustArchived(false);
                }}
              >
                Restore
              </Button>
            )
          }
        />
      </Card>

      <RegistrationCard
        organizationId={organizationId}
        vendor={data}
        onError={onError}
        onDone={() => {
          setError(null);
          invalidate();
        }}
      />

      <AliasCard
        organizationId={organizationId}
        vendor={data}
        onError={onError}
        onDone={() => {
          setError(null);
          invalidate();
        }}
      />

      <MergeCard
        organizationId={organizationId}
        vendor={data}
        candidates={(others.data?.items ?? []).filter((other) => other.id !== data.id)}
        onError={onError}
        onDone={() => {
          setError(null);
          invalidate();
        }}
      />

      <AtAGlancePanel organizationId={organizationId} entityType="vendor" entityId={data.id} />

      <RelationshipsPanel organizationId={organizationId} entityType="vendor" entityId={data.id} />

      <ObjectTimeline organizationId={organizationId} resourceType="vendor" resourceId={data.id} />
    </div>
  );
}

/**
 * A text field that saves when you leave it, and only if it changed. Uncontrolled
 * on purpose: a controlled input that re-renders from the server on every
 * keystroke fights the person typing into it.
 */
function TextField({
  label,
  hint,
  value,
  className,
  onCommit,
}: {
  label: string;
  hint?: string;
  value: string | null;
  className?: string;
  onCommit: (value: string) => void;
}) {
  return (
    <Field
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(className === undefined ? {} : { className })}
    >
      {({ inputProps }) => (
        <Input
          {...inputProps}
          defaultValue={value ?? ''}
          onBlur={(event) => {
            const next = event.target.value.trim();
            if (next !== (value ?? '')) onCommit(next);
          }}
        />
      )}
    </Field>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-3 py-2">
      <p className="text-xs text-content-muted">{label}</p>
      <p className="tabular text-lg font-semibold text-content">{value}</p>
    </div>
  );
}

interface SectionProps {
  readonly organizationId: string;
  readonly vendor: VendorDetail;
  readonly onError: (caught: unknown) => void;
  readonly onDone: () => void;
}

function RegistrationCard({ organizationId, vendor, onError, onDone }: SectionProps) {
  const [number, setNumber] = useState(vendor.gstHstNumber ?? '');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');

  const save = useMutation({
    mutationFn: () =>
      setVendorTaxInfo(organizationId, vendor.id, {
        gstHstNumber: number.trim() === '' ? null : number.trim().toUpperCase(),
        chargesGstHst: number.trim() !== '',
        effectiveFrom: from,
      }),
    onSuccess: onDone,
    onError,
  });

  // The registration in force, which is the only one there is any point
  // offering to check.
  const current: VendorDetail['taxInformation'][number] | undefined = vendor.taxInformation[0];

  // Narrowed once, here, so the JSX below needs no conditional that the
  // linter can read two ways.
  const unverified = current?.verifiedAt === null ? current : null;

  const verify = useMutation({
    mutationFn: () => verifyVendorTaxInfo(organizationId, vendor.id, unverified?.id ?? '', note),
    onSuccess: onDone,
    onError,
  });

  return (
    <Card>
      <CardHeader
        title="GST/HST registration"
        description="What decides whether a credit survives a review. Effective-dated, because a registration that lapsed in March was still good in February."
      />
      <CardBody className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Registration number"
            hint="Nine digits, optionally with RT0001."
            className="sm:col-span-2"
          >
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={number}
                onChange={(event) => setNumber(event.target.value)}
                placeholder="123456782RT0001"
              />
            )}
          </Field>
          <Field label="In force from">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => save.mutate()} isLoading={save.isPending}>
            Record it
          </Button>
          <span className="text-xs text-content-muted">
            Leave the number blank to record that this supplier is not registered.
          </span>
        </div>

        {vendor.taxInformation.length === 0 ? null : (
          <div className="border-t border-border pt-3">
            <p className="text-sm font-medium text-content">History</p>
            <ul className="mt-1 flex flex-col gap-1 text-sm text-content-muted">
              {vendor.taxInformation.map((entry) => (
                <li key={entry.id}>
                  {entry.gstHstNumber ?? 'Not registered'} · from {entry.effectiveFrom}
                  {entry.effectiveTo === null ? ' onward' : ` to ${entry.effectiveTo}`}
                  {entry.verifiedAt === null ? (
                    <span className="text-warning"> · not checked</span>
                  ) : (
                    <span className="text-success"> · checked {entry.verifiedAt.slice(0, 10)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {unverified === null ? null : (
          <div className="rounded-[var(--radius-surface)] border border-border bg-surface-sunken p-3">
            <p className="text-sm text-content">
              Checked it against the CRA registry? Record who, so somebody can be asked later.
            </p>
            <div className="mt-2 flex gap-2">
              <Input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Checked on the CRA registry by …"
              />
              <Button
                variant="secondary"
                onClick={() => verify.mutate()}
                disabled={note.trim().length < 10}
                isLoading={verify.isPending}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AliasCard({ organizationId, vendor, onError, onDone }: SectionProps) {
  const [alias, setAlias] = useState('');
  const remove = useMutation({
    mutationFn: (aliasId: string) => removeVendorAlias(organizationId, vendor.id, aliasId),
    onSuccess: onDone,
    onError,
  });

  const add = useMutation({
    mutationFn: () => addVendorAlias(organizationId, vendor.id, { rawAlias: alias.trim() }),
    onSuccess: () => {
      setAlias('');
      onDone();
    },
    onError,
  });

  return (
    <Card>
      <CardHeader
        title="Spellings that resolve here"
        description="Every one of these means a future receipt is matched without anybody being asked."
      />
      <CardBody className="flex flex-col gap-3">
        <ul className="flex flex-wrap gap-2">
          {vendor.aliases.map((entry) => (
            <li
              key={entry.id}
              className={cn(
                'rounded-full border px-3 py-1 text-xs',
                entry.humanConfirmed
                  ? 'border-success/40 bg-success/5 text-content'
                  : 'border-border text-content-muted',
              )}
              title={
                entry.humanConfirmed
                  ? 'Confirmed by a person, so nothing will overwrite it'
                  : 'Learned automatically'
              }
            >
              {entry.rawAlias}
              {entry.matchCount > 0 ? (
                <span className="ml-1 text-content-subtle">· {entry.matchCount}×</span>
              ) : null}
              {/* A spelling taught by mistake sends every future receipt to the
                  wrong supplier, so it has to be removable. */}
              <button
                type="button"
                aria-label={`Forget the spelling ${entry.rawAlias}`}
                title="Forget this spelling"
                onClick={() => remove.mutate(entry.id)}
                className="ml-2 text-content-subtle transition-colors hover:text-danger"
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        <div className="flex gap-2">
          <Input
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
            placeholder="EMCO MONCTON NB"
          />
          <Button
            variant="secondary"
            onClick={() => add.mutate()}
            disabled={alias.trim() === ''}
            isLoading={add.isPending}
          >
            Add spelling
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function MergeCard({
  organizationId,
  vendor,
  candidates,
  onError,
  onDone,
}: SectionProps & { candidates: { id: string; displayName: string }[] }) {
  const [sourceId, setSourceId] = useState('');
  const [reason, setReason] = useState('');

  const merge = useMutation({
    mutationFn: () => mergeVendors(organizationId, vendor.id, { sourceId, reason: reason.trim() }),
    onSuccess: () => {
      setSourceId('');
      setReason('');
      onDone();
    },
    onError,
  });

  return (
    <Card>
      <CardHeader
        title="Another supplier that is really this one"
        description="Moves every record and spelling here, and archives the other. Nothing is deleted."
      />
      <CardBody className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Merge in">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={sourceId}
                onChange={(event) => setSourceId(event.target.value)}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">Choose a supplier</option>
                {candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.displayName}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Why" hint="Somebody will ask why these two became one.">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Same company; Moncton is a branch."
              />
            )}
          </Field>
        </div>

        <div>
          <Button
            variant="danger"
            onClick={() => merge.mutate()}
            disabled={sourceId === '' || reason.trim().length < 3}
            isLoading={merge.isPending}
          >
            Merge
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
